import { observer } from 'mobx-react-lite'
import { useId } from 'react'
import { type BusinessInfoRequestedSupportingItemViewModel } from '../../BusinessInfoRequestedSupportingItemViewModel'
import { WizardContentBody, WizardContentFooter } from '@/components/Wizard'
import { TaxInfoGatheringWizardContentButtonGroupContainer } from '../../../components/TaxInfoGatheringWizardContentButtonGroupContainer'
import { type TaxInfoGatheringViewModel } from '../../../TaxInfoGatheringViewModel'
import { BusinessProfileEditor } from '@/features/tax-profile/business-profiles/components/BusinessProfileEditor'
import { BusinessProfileCardListSectionsContainer } from '../BusinessProfileCardListSection/BusinessProfileCardListSectionsContainer'

interface BusinessInfoWizardContentContainerProps {
  itemVm: BusinessInfoRequestedSupportingItemViewModel
  taxInfoGatheringVm: TaxInfoGatheringViewModel
}

export const BusinessInfoWizardContentContainer = observer(
  function BusinessInfoWizardContentContainer({
    itemVm,
    taxInfoGatheringVm,
  }: BusinessInfoWizardContentContainerProps) {
    const formId = useId()
    const isSkeleton = itemVm.isFetchingEditorData
    const isCompleteInfoGatheringPending =
      taxInfoGatheringVm.confirmCompleteInfoGathering.pending

    const hasNoExistingProfiles =
      itemVm.otherAvailableProfiles.length === 0 && !itemVm.profile

    // When user has no existing profiles, show the inline form
    if (hasNoExistingProfiles && !itemVm.isReadOnly) {
      return (
        <>
          <WizardContentBody>
            <BusinessProfileEditor
              formId={formId}
              isDisabled={
                itemVm.businessProfileEditorVm.save.pending ||
                isCompleteInfoGatheringPending
              }
              isSkeleton={isSkeleton}
              vm={itemVm.businessProfileEditorVm}
            />
          </WizardContentBody>
          <WizardContentFooter>
            <TaxInfoGatheringWizardContentButtonGroupContainer
              variant="save"
              formId={formId}
              isSkeleton={isSkeleton}
              isSavePending={itemVm.businessProfileEditorVm.save.pending}
              taxInfoGatheringVm={taxInfoGatheringVm}
              itemVm={itemVm}
            />
          </WizardContentFooter>
        </>
      )
    }

    // When user has existing profiles, show the selection view
    return (
      <>
        <WizardContentBody>
          <BusinessProfileCardListSectionsContainer
            vm={itemVm}
            isDisabled={isCompleteInfoGatheringPending}
          />
        </WizardContentBody>
        <WizardContentFooter>
          <TaxInfoGatheringWizardContentButtonGroupContainer
            variant="nav-only"
            isSkeleton={isSkeleton}
            taxInfoGatheringVm={taxInfoGatheringVm}
            itemVm={itemVm}
          />
        </WizardContentFooter>
      </>
    )
  },
)
